/**
 * @description
 * MailerSend email configuration settings component
 *
 * @author AI Developer <ai@byteable.ai>
 * @date 2025-01-06
 */

import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { HelpCircle, ExternalLink } from 'lucide-react';
import { api } from '../../services/api';
import { EmailSettingsProps, EmailSettingsState } from './types';

/**
 * Email settings component with self-contained state and logic
 */
export function EmailSettings({ onRefresh }: EmailSettingsProps) {
  const [mailerSendSettings, setMailerSendSettings] = useState<EmailSettingsState>({
    apiKey: '',
    apiUrl: 'https://api.mailersend.com/v1/email',
    fromAddress: 'noreply@byteable.ai',
    fromName: 'Byte Platform'
  });
  const [mailerSendLoading, setMailerSendLoading] = useState(false);

  useEffect(() => {
    loadMailerSendSettings();
  }, []);

  const loadMailerSendSettings = async () => {
    try {
      const settings = await api.getSettings();
      const emailSettingsData = settings.filter(s => (s.category ?? '') === 'email');
      
      const newMailerSendSettings = {
        apiKey: emailSettingsData.find(s => s.key === 'MAILERSEND_API_KEY')?.value || '',
        apiUrl: emailSettingsData.find(s => s.key === 'MAILERSEND_API_URL')?.value || 'https://api.mailersend.com/v1/email',
        fromAddress: emailSettingsData.find(s => s.key === 'EMAIL_FROM_ADDRESS')?.value || 'noreply@byteable.ai',
        fromName: emailSettingsData.find(s => s.key === 'EMAIL_FROM_NAME')?.value || 'Byte Platform'
      };
      
      setMailerSendSettings(newMailerSendSettings);
    } catch (error) {
      console.error('Error loading MailerSend settings:', error);
      toast.error('Failed to load MailerSend settings');
    }
  };

  const saveMailerSendSettings = async () => {
    try {
      setMailerSendLoading(true);
      
      const mailerSendSettingsToSave = [
        { key: 'MAILERSEND_API_KEY', value: mailerSendSettings.apiKey, category: 'email', isSecret: true, description: 'MailerSend API key for email delivery' },
        { key: 'MAILERSEND_API_URL', value: mailerSendSettings.apiUrl, category: 'email', isSecret: false, description: 'MailerSend API endpoint URL' },
        { key: 'EMAIL_FROM_ADDRESS', value: mailerSendSettings.fromAddress, category: 'email', isSecret: false, description: 'Default sender email address' },
        { key: 'EMAIL_FROM_NAME', value: mailerSendSettings.fromName, category: 'email', isSecret: false, description: 'Default sender name' }
      ];

      await api.updateSettings(mailerSendSettingsToSave);

      toast.success('MailerSend settings saved successfully');
      onRefresh(); // Refresh parent settings
    } catch (error) {
      console.error('Error saving MailerSend settings:', error);
      toast.error('Failed to save MailerSend settings');
    } finally {
      setMailerSendLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* MailerSend Settings Header with Help */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">MailerSend Email Configuration</h3>
        <a
          href="/docs/stripe/DOCUMENTATION.md"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
          title="Open Email Service Documentation"
        >
          <HelpCircle className="h-4 w-4" />
          <span>Help</span>
          <ExternalLink className="h-3 w-3" />
        </a>
      </div>

      {/* MailerSend API Configuration */}
      <div className="rounded-lg border p-4">
        <h3 className="text-md font-medium mb-3">MailerSend API Configuration</h3>
        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium">API Key</label>
            <p className="text-xs text-muted-foreground mt-1">Your MailerSend API key for email delivery</p>
            <input
              type="password"
              value={mailerSendSettings.apiKey}
              onChange={(e) => setMailerSendSettings(prev => ({ ...prev, apiKey: e.target.value }))}
              placeholder="mlsn.xxx..."
              className="mt-2 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
          <div>
            <label className="text-sm font-medium">API URL</label>
            <p className="text-xs text-muted-foreground mt-1">MailerSend API endpoint URL</p>
            <input
              type="text"
              value={mailerSendSettings.apiUrl}
              onChange={(e) => setMailerSendSettings(prev => ({ ...prev, apiUrl: e.target.value }))}
              placeholder="https://api.mailersend.com/v1/email"
              className="mt-2 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
        </div>
      </div>

      {/* Email Settings */}
      <div className="rounded-lg border p-4">
        <h3 className="text-md font-medium mb-3">Email Settings</h3>
        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium">From Address</label>
            <p className="text-xs text-muted-foreground mt-1">Default sender email address</p>
            <input
              type="email"
              value={mailerSendSettings.fromAddress}
              onChange={(e) => setMailerSendSettings(prev => ({ ...prev, fromAddress: e.target.value }))}
              placeholder="noreply@byteable.ai"
              className="mt-2 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
          <div>
            <label className="text-sm font-medium">From Name</label>
            <p className="text-xs text-muted-foreground mt-1">Default sender name</p>
            <input
              type="text"
              value={mailerSendSettings.fromName}
              onChange={(e) => setMailerSendSettings(prev => ({ ...prev, fromName: e.target.value }))}
              placeholder="Byte Platform"
              className="mt-2 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="rounded-lg border p-4">
        <h3 className="text-md font-medium mb-3">Actions</h3>
        <div className="flex flex-wrap gap-2">
          <button 
            onClick={saveMailerSendSettings}
            disabled={mailerSendLoading}
            className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {mailerSendLoading ? 'Saving...' : 'Save Configuration'}
          </button>
        </div>
      </div>
    </div>
  );
}
